import React from 'react';
import { useForm } from 'react-hook-form';
import { Button, Divider, Form } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from './auth-layout';
import {
  FormInput,
  FormPassword,
  LoadingState,
  Space,
  useLoading,
} from '@monorepo/react-components';
import { formEmailValidation, formPasswordValidation } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { useNotification } from '../../hooks/use-notification';
import GoogleLoginButton from './social/google-login-button';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    dataStore: { userStore },
  } = useStore();
  const form = useForm<RegisterFormData>();
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Initial);
  const notificationApi = useNotification();

  const onSubmit = async (data: RegisterFormData) => {
    try {
      updateLoadingState(LoadingState.Loading);
      await userStore.register(data);

      notificationApi.success({
        placement: 'top',
        icon: '',
        message: t('common.success'),
        description: t('auth.confirm.success'),
      });

      navigate('/login');
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        icon: '',
        message: t('common.error'),
        description: t('auth.login.errors.invalidCredentials'),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  return (
    <AuthLayout title={t('auth.register.title')}>
      <Helmet>
        <title>Create Account | ShortsNinja</title>
        <meta
          name="description"
          content="Join ShortsNinja today. Create automated faceless videos with AI voiceover to scale your content creation."
        />
        <meta
          name="keywords"
          content="sign up, create account, register, ShortsNinja, short video management, faceless videos, ai video automation, ai voiceover, content automation"
        />
      </Helmet>
      <Form onFinish={form.handleSubmit(onSubmit)} layout="vertical">
        <Space size={'large'} direction={'vertical'}>
          <FormInput
            placeholder={t('auth.register.emailPlaceholder')}
            form={form}
            controllerProps={{
              name: 'email',
              rules: formEmailValidation(),
            }}
          />
          <FormPassword
            placeholder={t('auth.register.passwordPlaceholder')}
            form={form}
            controllerProps={{
              name: 'password',
              rules: formPasswordValidation(),
            }}
          />
          <FormPassword
            placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
            form={form}
            controllerProps={{
              name: 'confirmPassword',
              rules: {
                ...formPasswordValidation(),
                validate: (value: string) =>
                  value === form.getValues('password') ||
                  t('auth.resetPassword.mismatch'),
              },
            }}
          />
          <Button
            loading={loadingState === LoadingState.Loading}
            type="primary"
            htmlType="submit"
            block
          >
            {t('auth.register.registerButton')}
          </Button>
        </Space>
      </Form>
      <Divider plain>{t('common.or')}</Divider>
      <GoogleLoginButton />
      <div className="auth-link">
        {t('auth.register.alreadyHaveAccount')}{' '}
        <Link to="/login">{t('auth.register.logIn')}</Link>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;
